import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
    Link,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Slider from "react-slick";
import Authorize from "../Authorize";
import * as Roles from "../../resources/UserRole";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import notFoundImage from "../../img/no_image_placeholder_icon.png";

const SlickArrow = ({ direction, onClick, disabled }) => (
    <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
            },
            ...(direction === "left" ? { left: -16 } : { right: -16 }),
        }}
    >
        {direction === "left" ? (
            <ChevronLeftIcon size={20} />
        ) : (
            <ChevronRightIcon size={20} />
        )}
    </IconButton>
);

const RelatedProductCard = ({
    product,
    selectedCustomer,
    cartLines,
    maxLines,
    localized,
    handleCartClick,
}) => (
    <Box key={product.id} sx={{ p: 1 }}>
        <Card
            sx={{
                height: "100%",
                width: "100%",
                maxWidth: "320px",
                minWidth: "280px",
            }}
        >
            {/* Association Type */}
            <Box sx={{ p: 1, pb: 3 }}>
                <Typography
                    variant="body2"
                    component="div"
                    fontWeight={600}
                    sx={{
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        padding: "10px 14px",
                        fontSize: "1.125rem",
                        maxWidth: "160px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        lineHeight: 1.2,
                    }}
                >
                    {product.associationType}
                </Typography>
            </Box>

            {/* Product Image */}
            <CardMedia
                component="img"
                height="200"
                image={`${product.imageUrl}300x250px@1x/${product.sku}.jpg?q=80&x.template=y&crop=false`}
                alt={product.name}
                sx={{ objectFit: "contain" }}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = notFoundImage;
                }}
            />

            <CardContent>
                {/* Product Link */}
                <Link
                    href={`/product/${encodeURIComponent(product.itemNumberShort)}`}
                    state={{ selectedCustomer }}
                    sx={{
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        mb: 0.5,
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    {product.sku}
                </Link>

                {/* Product Name */}
                <Typography
                    variant="h6"
                    component="h3"
                    noWrap
                    sx={{
                        fontSize: "1.5rem",
                        fontWeight: 500,
                        lineHeight: 1.3,
                        mb: 1,
                    }}
                >
                    {product.name}
                </Typography>

                {/* Product Description */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mt: 1,
                        height: {
                            xs: 48,
                            sm: 48,
                            md: 56,
                            lg: 64,
                        },
                        overflow: "hidden",
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            width: "100%",
                            fontSize: "1.25rem",
                            lineHeight: 1.4,
                            fontWeight: 400,
                        }}
                    >
                        {product.description}
                    </Typography>
                </Box>

                {/* Product Actions */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        mt: 1,
                    }}
                >
                    {(selectedCustomer.addressType == 0 ||
                        selectedCustomer.addressType == 2 ||
                        selectedCustomer.addressType == 3) && (
                            <Authorize hasRoles={[Roles.PLACE_ORDERS]} displayMessage={false}>
                                {cartLines < maxLines ? (
                                    <Box
                                        className="button-wrapper add-to-order"
                                        sx={{
                                            minWidth: "auto",
                                            width: "auto",
                                            height: "auto",
                                        }}
                                    >
                                        <Button
                                            sx={{
                                                padding: "0.725rem 1.5rem !important",
                                                minWidth: "auto !important",
                                                width: "auto !important",
                                                whiteSpace: "nowrap !important",
                                                overflow: "visible !important",
                                                height: "auto !important",
                                                borderRadius: "0",
                                            }}
                                            className="button"
                                            onClick={(e) => handleCartClick(e, product)}
                                        >
                                            {localized.ButtonText_AddToOrder}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box
                                        style={{
                                            whiteSpace: "nowrap",
                                            fontSize: "large",
                                            marginTop: "4px",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faExclamationTriangle}
                                            color="orange"
                                        />{" "}
                                        {localized.CartMaxLines}
                                    </Box>
                                )}
                            </Authorize>
                        )}
                </Box>
            </CardContent>
        </Card>
    </Box>
);

const RelatedItemsHeader = ({
    localized,
    onPrevClick,
    onNextClick,
    showMobileArrows,
}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 2,
        }}
    >
        <Typography variant="h5" component="h2" fontWeight={600}>
            {localized.RelatedItemsHeader}
        </Typography>

        {showMobileArrows && (
            <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                    onClick={onPrevClick}
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                    }}
                >
                    <ChevronLeftIcon size={20} />
                </IconButton>
                <IconButton
                    onClick={onNextClick}
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                    }}
                >
                    <ChevronRightIcon size={20} />
                </IconButton>
            </Box>
        )}
    </Box>
);

const RelatedProducts = ({
    products,
    cartActions,
    localized,
    selectedCustomer,
    cartLines,
    maxLines,
}) => {
    const [sliderRef, setSliderRef] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const slidesToShow = isMobile ? 1 : Math.min(products?.length || 1, 3);
    const shouldShowNavigation = products?.length > slidesToShow;
    const [currentSlide, setCurrentSlide] = useState(0);
    const totalSlides = products?.length || 0;
    const isAtStart = currentSlide === 0;
    const isAtEnd = currentSlide >= totalSlides - slidesToShow
    const handleCartClick = useCallback(
        (event, product) => {
            event.stopPropagation();
            const item = { product: product, quantity: 1 };
            cartActions.AddItemToCart(item, localized);
        },
        [cartActions, localized]
    );

    const handlePrevClick = () => {
        if (sliderRef) {
            sliderRef.slickPrev();
        }
    };

    const handleNextClick = () => {
        if (sliderRef) {
            sliderRef.slickNext();
        }
    };
    const sliderSettings = {

        beforeChange: (oldIndex, newIndex) => {
            setCurrentSlide(newIndex);
        },

        dots: false,
        speed: 400,
        infinite: false,
        centerMode: false,
        slidesToShow: slidesToShow,
        slidesToScroll: slidesToShow,
        variableWidth: false,
        prevArrow: shouldShowNavigation && !isMobile ? (
            <SlickArrow direction="left" onClick={handlePrevClick} disabled={isAtStart} />
        ) : null,
        nextArrow: shouldShowNavigation && !isMobile ? (
            <SlickArrow direction="right" onClick={handleNextClick} disabled={isAtEnd} />
        ) : null,

        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 3,
                    infinite: false,
                },
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 2,
                    infinite: false,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: false,
                },
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: false,
                    centerMode: false,
                },
            },
        ],
    };

    return (
        <Box
            sx={{
                my: 3,
                mx: "auto",
                width: "100%",
                maxWidth: "1400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <RelatedItemsHeader
                localized={localized}
                onPrevClick={handlePrevClick}
                onNextClick={handleNextClick}
                showMobileArrows={isMobile && shouldShowNavigation}
            />

            {products && products.length > 0 && (
                <Box
                    sx={{
                        width: "100%",
                    }}
                >
                    <Slider ref={setSliderRef} {...sliderSettings}>
                        {products.map((product) => (
                            <RelatedProductCard
                                key={product.id}
                                product={product}
                                selectedCustomer={selectedCustomer}
                                cartLines={cartLines}
                                maxLines={maxLines}
                                localized={localized}
                                handleCartClick={handleCartClick}
                            />
                        ))}
                    </Slider>
                </Box>
            )}
        </Box>
    );
};

export default RelatedProducts;
